/**
 * Text Pressure — vanilla port of React Bits TextPressure
 * https://github.com/DavidHDev/react-bits (TextAnimations/TextPressure)
 *
 * Usage:
 *   <div class="text-pressure" data-text-pressure data-text="Done." data-color="#fff"
 *        data-fixed-size="true">
 *     <div class="text-pressure__title"></div>
 *   </div>
 */
(function (global) {
  "use strict";

  function dist(a, b) {
    var dx = b.x - a.x;
    var dy = b.y - a.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Closer → higher value. Far away settles at minVal (must stay readable).
  function getAttr(distance, maxDist, minVal, maxVal) {
    var t = 1 - Math.min(1, Math.max(0, distance / maxDist));
    return minVal + (maxVal - minVal) * t;
  }

  function parseBool(v, fallback) {
    if (v == null || v === "") return fallback;
    if (v === true || v === false) return v;
    return String(v).toLowerCase() !== "false" && v !== "0";
  }

  function num(v, fallback) {
    if (v == null || v === "") return fallback;
    var n = Number(v);
    return isFinite(n) ? n : fallback;
  }

  function TextPressure(el, options) {
    options = options || {};
    this.el = el;
    this.title =
      el.querySelector(".text-pressure__title") ||
      el.querySelector("[data-text-pressure-title]") ||
      el;
    this.opts = {
      text: options.text != null ? String(options.text) : el.getAttribute("data-text") || "Done.",
      color: options.color || el.getAttribute("data-color") || "#ffffff",
      width: parseBool(options.width != null ? options.width : el.getAttribute("data-width"), true),
      weight: parseBool(options.weight != null ? options.weight : el.getAttribute("data-weight"), true),
      italic: parseBool(options.italic != null ? options.italic : el.getAttribute("data-italic"), false),
      fixedSize: parseBool(
        options.fixedSize != null ? options.fixedSize : el.getAttribute("data-fixed-size"),
        false
      ),
      minFontSize: num(options.minFontSize != null ? options.minFontSize : el.getAttribute("data-min-font-size"), 24),
      // Resting (far) → peak (near). Keep rest readable on marketing pages.
      minWidth: num(options.minWidth != null ? options.minWidth : el.getAttribute("data-min-width"), 90),
      maxWidth: num(options.maxWidth != null ? options.maxWidth : el.getAttribute("data-max-width"), 160),
      minWeight: num(options.minWeight != null ? options.minWeight : el.getAttribute("data-min-weight"), 550),
      maxWeight: num(options.maxWeight != null ? options.maxWeight : el.getAttribute("data-max-weight"), 900)
    };

    this.mouse = { x: 0, y: 0 };
    this.cursor = { x: 0, y: 0 };
    this.spans = [];
    this.rafId = 0;
    this._onMove = this._onMove.bind(this);
    this._onTouch = this._onTouch.bind(this);
    this._onResize = this._onResize.bind(this);
    this._animate = this._animate.bind(this);

    this._build();
    this._bind();
    this._animate();
  }

  TextPressure.prototype._build = function () {
    var text = this.opts.text;
    var title = this.title;
    title.innerHTML = "";
    title.setAttribute("aria-label", text);
    title.style.color = this.opts.color;
    title.style.fontFamily = '"Roboto Flex", sans-serif';
    this.spans = [];
    var chars = text.split("");
    for (var i = 0; i < chars.length; i++) {
      var span = document.createElement("span");
      span.setAttribute("data-char", chars[i]);
      span.textContent = chars[i] === " " ? "\u00A0" : chars[i];
      span.style.color = this.opts.color;
      span.style.fontFamily = '"Roboto Flex", sans-serif';
      span.style.display = "inline-block";
      span.style.willChange = "transform, font-variation-settings";
      span.style.transformOrigin = "center bottom";
      title.appendChild(span);
      this.spans.push(span);
    }
    this._setSize();
  };

  TextPressure.prototype._setSize = function () {
    if (this.opts.fixedSize) {
      // Keep CSS font-size (e.g. theme section scale rules)
      this.title.style.fontSize = "";
      return;
    }
    var rect = this.el.getBoundingClientRect();
    var n = Math.max(this.spans.length, 1);
    var size = Math.max(rect.width / (n / 2), this.opts.minFontSize);
    this.title.style.fontSize = size + "px";
  };

  TextPressure.prototype._bind = function () {
    var rect = this.el.getBoundingClientRect();
    this.mouse.x = this.cursor.x = rect.left + rect.width / 2;
    this.mouse.y = this.cursor.y = rect.top + rect.height / 2;

    window.addEventListener("mousemove", this._onMove);
    window.addEventListener("touchmove", this._onTouch, { passive: true });
    window.addEventListener("resize", this._onResize);
  };

  TextPressure.prototype._onMove = function (e) {
    this.cursor.x = e.clientX;
    this.cursor.y = e.clientY;
  };

  TextPressure.prototype._onTouch = function (e) {
    if (!e.touches || !e.touches[0]) return;
    this.cursor.x = e.touches[0].clientX;
    this.cursor.y = e.touches[0].clientY;
  };

  TextPressure.prototype._onResize = function () {
    this._setSize();
  };

  TextPressure.prototype._animate = function () {
    this.mouse.x += (this.cursor.x - this.mouse.x) / 15;
    this.mouse.y += (this.cursor.y - this.mouse.y) / 15;

    var titleRect = this.title.getBoundingClientRect();
    // Larger radius so moving near the headline clearly reacts
    var maxDist = Math.max(titleRect.width * 0.85, titleRect.height * 2.5, 120);
    var o = this.opts;

    for (var i = 0; i < this.spans.length; i++) {
      var span = this.spans[i];
      var r = span.getBoundingClientRect();
      var center = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
      var d = dist(this.mouse, center);
      var closeness = 1 - Math.min(1, Math.max(0, d / maxDist));

      var wdth = o.width ? Math.floor(getAttr(d, maxDist, o.minWidth, o.maxWidth)) : 100;
      var wght = o.weight ? Math.floor(getAttr(d, maxDist, o.minWeight, o.maxWeight)) : 600;
      var ital = o.italic ? getAttr(d, maxDist, 0, 0.7).toFixed(2) : 0;

      // Variable font axes (needs Roboto Flex). Scale always visible as fallback.
      span.style.fontVariationSettings =
        "'wght' " + wght + ", 'wdth' " + wdth + ", 'ital' " + ital + ", 'opsz' 144";
      span.style.transform =
        "scale(" +
        (1 + closeness * 0.42) +
        ") translateY(" +
        (-closeness * 10) +
        "px)";
    }

    this.rafId = requestAnimationFrame(this._animate);
  };

  TextPressure.prototype.setText = function (text) {
    this.opts.text = String(text);
    this._build();
  };

  TextPressure.prototype.setColor = function (color) {
    this.opts.color = color;
    this.title.style.color = color;
    for (var i = 0; i < this.spans.length; i++) {
      this.spans[i].style.color = color;
    }
  };

  TextPressure.prototype.destroy = function () {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("mousemove", this._onMove);
    window.removeEventListener("touchmove", this._onTouch);
    window.removeEventListener("resize", this._onResize);
  };

  TextPressure.init = function (el, options) {
    if (!el) return null;
    return new TextPressure(el, options);
  };

  TextPressure.autoInit = function (root) {
    root = root || document;
    var nodes = root.querySelectorAll("[data-text-pressure]");
    var instances = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__textPressure) continue;
      var inst = new TextPressure(nodes[i]);
      nodes[i].__textPressure = inst;
      instances.push(inst);
    }
    return instances;
  };

  global.TextPressure = TextPressure;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      TextPressure.autoInit();
    });
  } else {
    TextPressure.autoInit();
  }
})(typeof window !== "undefined" ? window : this);
